import React, { useEffect, useState } from "react";
import {
    Form,
    Row,
    Col,
    Select,
    InputNumber,
    Button,
    Card,
    message,
    Flex,
    Alert,
    Grid
} from "antd";

const { useBreakpoint } = Grid;

import { useDispatch, useSelector } from "react-redux";

import {
    saveCuttingLogic,
    fetchCuttingLogic
} from "../cuttingLogicThunk";

import {
    setFormField,
    resetForm,
    clearParser
} from "../cuttingLogicSlice";
import { getAccessType } from "../../../../lib/functions";

const CuttingLogicForm = () => {
    const dispatch = useDispatch();
    const screens = useBreakpoint();
    const isMobile = !screens.sm;
    const [loading, setLoading] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [backendError, setBackendError] = useState(null);
    let [access_type, access_type_set] = useState("");

    useEffect(() => {
        const access = getAccessType("purchase_entry");
        access_type_set(access);
    }, []);


    const {
        id,
        manufacturers,
        logicList,
        selectedManufacturer,
        gtin_from,
        gtin_to,
        batch_from,
        batch_to,
        expiry_from,
        expiry_to,
        serial_from,
        serial_to,
        saving
    } = useSelector(s => s.cuttingLogic);

    // Validation helper functions
    const isValidPositiveInt = (value) => {
        return value !== null &&
            value !== undefined &&
            value !== '' &&
            Number.isInteger(Number(value)) &&
            Number(value) > 0;
    };

    // Check if manufacturer already has a logic
    const isManufacturerAlreadyConfigured = (manufacturerId, currentId) => {
        const existingLogic = logicList.find(
            logic => logic.manufacture_id === manufacturerId &&
                logic.is_deleted === 0 &&
                (currentId ? logic.id !== currentId : true)
        );
        return !!existingLogic;
    };

    const validateAllFields = () => {
        const errors = [];
        setBackendError(null); // Clear previous backend error

        // 1. Check if manufacturer is selected
        if (!selectedManufacturer) {
            errors.push("Please select a manufacturer");
        }

        // 2. Check if manufacturer already has a logic
        if (selectedManufacturer) {
            if (!id && isManufacturerAlreadyConfigured(selectedManufacturer)) {
                errors.push("This manufacturer already has a cutting logic configured. Please edit the existing one.");
            }

            if (id) {
                const currentRecord = logicList.find(logic => logic.id === id);
                if (currentRecord && currentRecord.manufacture_id !== selectedManufacturer) {
                    if (isManufacturerAlreadyConfigured(selectedManufacturer, id)) {
                        errors.push("This manufacturer already has a cutting logic configured. Please edit the existing one.");
                    }
                }
            }
        }

        // 3. Check all fields are filled and valid
        const fields = [
            { value: gtin_from, label: 'GTIN From' },
            { value: gtin_to, label: 'GTIN To' },
            { value: batch_from, label: 'Batch From' },
            { value: batch_to, label: 'Batch To' },
            { value: expiry_from, label: 'Expiry From' },
            { value: expiry_to, label: 'Expiry To' },
            { value: serial_from, label: 'Serial From' },
            { value: serial_to, label: 'Serial To' }
        ];

        for (const field of fields) {
            if (!isValidPositiveInt(field.value)) {
                errors.push(`${field.label} must be a positive integer greater than 0`);
            }
        }

        // 4. Check From < To for each pair
        const pairs = [
            { from: gtin_from, to: gtin_to, label: 'GTIN' },
            { from: batch_from, to: batch_to, label: 'Batch' },
            { from: expiry_from, to: expiry_to, label: 'Expiry' },
            { from: serial_from, to: serial_to, label: 'Serial' }
        ];

        for (const pair of pairs) {
            if (isValidPositiveInt(pair.from) && isValidPositiveInt(pair.to)) {
                if (Number(pair.from) >= Number(pair.to)) {
                    errors.push(`${pair.label} From must be less than ${pair.label} To`);
                }
            }
        }

        // 5. Check for overlapping ranges
        const ranges = [
            { from: Number(gtin_from), to: Number(gtin_to), label: 'GTIN' },
            { from: Number(batch_from), to: Number(batch_to), label: 'Batch' },
            { from: Number(expiry_from), to: Number(expiry_to), label: 'Expiry' },
            { from: Number(serial_from), to: Number(serial_to), label: 'Serial' }
        ];

        const allValid = ranges.every(r => r.from > 0 && r.to > 0 && r.from < r.to);

        if (allValid) {
            for (let i = 0; i < ranges.length; i++) {
                for (let j = i + 1; j < ranges.length; j++) {
                    const range1 = ranges[i];
                    const range2 = ranges[j];

                    const overlap = !(range1.to < range2.from || range2.to < range1.from);

                    if (overlap) {
                        errors.push(
                            `${range1.label} range (${range1.from}-${range1.to}) overlaps with ${range2.label} range (${range2.from}-${range2.to})`
                        );
                    }
                }
            }
        }

        setValidationErrors(errors);
        return errors.length === 0;
    };

    const handleSave = () => {
        setBackendError(null); // Clear previous backend error

        if (!validateAllFields()) {
            if (validationErrors.length > 0) {
                message.warning(validationErrors[0]);
            }
            return;
        }

        setLoading(true);

        const payload = {
            id,
            manufacture_id: selectedManufacturer,
            gtin_from: Number(gtin_from),
            gtin_to: Number(gtin_to),
            batch_from: Number(batch_from),
            batch_to: Number(batch_to),
            expiry_from: Number(expiry_from),
            expiry_to: Number(expiry_to),
            serial_from: Number(serial_from),
            serial_to: Number(serial_to)
        };

        dispatch(saveCuttingLogic(payload))
            .unwrap()
            .then((res) => {
                message.success(res?.message || (id ? "Updated Successfully" : "Saved Successfully"));
                dispatch(fetchCuttingLogic());
                dispatch(resetForm());
                setValidationErrors([]);
                setBackendError(null);
            })
            .catch((error) => {
                // Handle backend errors
                console.error('Backend Error:', error);

                // Check if error has a message from backend
                if (error?.message) {
                    setBackendError(error.message);
                    message.error(error.message);
                } else if (typeof error === 'string') {
                    setBackendError(error);
                    message.error(error);
                } else {
                    const errorMsg = "Failed to save. Please try again.";
                    setBackendError(errorMsg);
                    message.error(errorMsg);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleReset = () => {
        dispatch(resetForm());
        setValidationErrors([]);
        setBackendError(null);
    };

    // Filter manufacturers
    const getAvailableManufacturers = () => {
        if (id) {
            return manufacturers;
        }
        const configuredManufacturerIds = logicList
            .filter(logic => logic.is_deleted === 0)
            .map(logic => logic.manufacture_id);

        return manufacturers.filter(m => !configuredManufacturerIds.includes(m.id));
    };

    const isManufacturerConfigured = (manufacturerId) => {
        return logicList.some(
            l => l.manufacture_id === manufacturerId &&
                l.is_deleted === 0 &&
                (id ? l.id !== id : true)
        );
    };

    return (
        <Card title="Manufacture Cutting Logic">
            <Form layout="vertical">
                {/* Show backend errors */}
                {backendError && (
                    <Alert
                        message="Error"
                        description={backendError}
                        type="error"
                        showIcon
                        closable
                        style={{ marginBottom: 16 }}
                        onClose={() => setBackendError(null)}
                    />
                )}

                {/* Show validation errors */}
                {validationErrors.length > 0 && (
                    <Alert
                        message="Validation Errors"
                        description={
                            <ul style={{ margin: 0, paddingLeft: 20 }}>
                                {validationErrors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        }
                        type="warning"
                        showIcon
                        closable
                        style={{ marginBottom: 16 }}
                        onClose={() => setValidationErrors([])}
                    />
                )}

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={24} md={12} lg={8}>
                        <Form.Item
                            label="Manufacturer"
                            required
                            validateStatus={selectedManufacturer ? 'success' : ''}
                            help={id ? "Changing manufacturer will validate if the new manufacturer already has a logic" : ""}
                        >
                            <Select
                                showSearch
                                placeholder="Select Manufacturer"
                                value={selectedManufacturer}
                                options={getAvailableManufacturers().map(m => ({
                                    label: m.name + (isManufacturerConfigured(m.id) ? " (Already configured)" : ""),
                                    value: m.id,
                                    disabled: isManufacturerConfigured(m.id)
                                }))}
                                onChange={(value) => {
                                    dispatch(setFormField({
                                        field: "selectedManufacturer",
                                        value
                                    }));
                                    dispatch(clearParser());
                                    setValidationErrors([]);
                                    setBackendError(null);
                                }}
                                filterOption={(input, option) =>
                                    option.label.toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            label="(01) GTIN From"
                            required
                            validateStatus={gtin_from && isValidPositiveInt(gtin_from) ? 'success' : ''}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={1}
                                step={1}
                                precision={0}
                                value={gtin_from}
                                placeholder="Enter GTIN From"
                                onChange={(value) => {
                                    dispatch(setFormField({
                                        field: "gtin_from",
                                        value
                                    }));
                                    setValidationErrors([]);
                                    setBackendError(null);
                                }}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            label="(01) GTIN To"
                            required
                            validateStatus={gtin_to && isValidPositiveInt(gtin_to) ? 'success' : ''}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={1}
                                step={1}
                                precision={0}
                                value={gtin_to}
                                placeholder="Enter GTIN To"
                                onChange={(value) => {
                                    dispatch(setFormField({
                                        field: "gtin_to",
                                        value
                                    }));
                                    setValidationErrors([]);
                                    setBackendError(null);
                                }}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            label="(10) Batch From"
                            required
                            validateStatus={batch_from && isValidPositiveInt(batch_from) ? 'success' : ''}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={1}
                                step={1}
                                precision={0}
                                value={batch_from}
                                placeholder="Enter Batch From"
                                onChange={(value) => {
                                    dispatch(setFormField({
                                        field: "batch_from",
                                        value
                                    }));
                                    setValidationErrors([]);
                                    setBackendError(null);
                                }}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            label="(10) Batch To"
                            required
                            validateStatus={batch_to && isValidPositiveInt(batch_to) ? 'success' : ''}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={1}
                                step={1}
                                precision={0}
                                value={batch_to}
                                placeholder="Enter Batch To"
                                onChange={(value) => {
                                    dispatch(setFormField({
                                        field: "batch_to",
                                        value
                                    }));
                                    setValidationErrors([]);
                                    setBackendError(null);
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            label="(17) Expiry From"
                            required
                            validateStatus={expiry_from && isValidPositiveInt(expiry_from) ? 'success' : ''}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={1}
                                step={1}
                                precision={0}
                                value={expiry_from}
                                placeholder="Enter Expiry From"
                                onChange={(value) => {
                                    dispatch(setFormField({
                                        field: "expiry_from",
                                        value
                                    }));
                                    setValidationErrors([]);
                                    setBackendError(null);
                                }}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            label="(17) Expiry To"
                            required
                            validateStatus={expiry_to && isValidPositiveInt(expiry_to) ? 'success' : ''}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={1}
                                step={1}
                                precision={0}
                                value={expiry_to}
                                placeholder="Enter Expiry To"
                                onChange={(value) => {
                                    dispatch(setFormField({
                                        field: "expiry_to",
                                        value
                                    }));
                                    setValidationErrors([]);
                                    setBackendError(null);
                                }}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            label="(21) Serial From"
                            required
                            validateStatus={serial_from && isValidPositiveInt(serial_from) ? 'success' : ''}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={1}
                                step={1}
                                precision={0}
                                value={serial_from}
                                placeholder="Enter Serial From"
                                onChange={(value) => {
                                    dispatch(setFormField({
                                        field: "serial_from",
                                        value
                                    }));
                                    setValidationErrors([]);
                                    setBackendError(null);
                                }}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={6}>
                        <Form.Item
                            label="(21) Serial To"
                            required
                            validateStatus={serial_to && isValidPositiveInt(serial_to) ? 'success' : ''}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={1}
                                step={1}
                                precision={0}
                                value={serial_to}
                                placeholder="Enter Serial To"
                                onChange={(value) => {
                                    dispatch(setFormField({
                                        field: "serial_to",
                                        value
                                    }));
                                    setValidationErrors([]);
                                    setBackendError(null);
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Flex justify="end" wrap="wrap" gap={10} style={{ marginTop: 16 }}>
                    <Button onClick={handleReset} style={isMobile ? { flex: 1 } : undefined}>
                        Reset
                    </Button>
                    {access_type == "WRITE" && (
                        <Button
                            type="primary"
                            style={isMobile ? { flex: 1 } : undefined}
                            onClick={handleSave}
                            loading={loading || saving}
                        >
                            {id ? "Update" : "Save"}
                        </Button>)}

                </Flex>
            </Form>
        </Card>
    );
};

export default CuttingLogicForm;